import React from 'react';
import { PosePreset, CataloguePoseData } from '../types';

interface PoseGridProps {
    poses: PosePreset[];
    loading: boolean;
    selectedPoses: CataloguePoseData[];
    onTogglePose: (pose: PosePreset) => void;
    genderFilter: 'female' | 'male';
    onGenderChange: (gender: 'female' | 'male') => void;
    typeFilter: string;
    onTypeChange: (type: string) => void;
    sizeFilter: 'normal' | 'plus';
    onSizeChange: (size: 'normal' | 'plus') => void;
}

export const PoseGrid: React.FC<PoseGridProps> = ({
    poses,
    loading,
    selectedPoses,
    onTogglePose,
    genderFilter,
    onGenderChange,
    typeFilter,
    onTypeChange,
    sizeFilter,
    onSizeChange,
}) => {
    const filteredPoses = poses.filter((p) => {
        const matchGender = p.gender?.toLowerCase() === genderFilter;
        const matchType = p.type?.toLowerCase() === typeFilter;
        const id = (p.poseId || '').toUpperCase();
        const isPlusSize = id.startsWith('PF') || id.startsWith('PM');
        const matchSize = sizeFilter === 'plus' ? isPlusSize : !isPlusSize;
        return matchGender && matchType && matchSize;
    });

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Select Poses</h3>
                    <p className="text-sm text-gray-500">Choose up to 10 poses (use Next Step when done)</p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    {(['standing', 'sitting', 'leaning', 'back'] as const).map((type) => (
                        <button
                            key={type}
                            onClick={() => onTypeChange(type)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${typeFilter === type ? 'bg-secondary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            {type}
                        </button>
                    ))}
                    <div className="flex bg-gray-100 rounded-full p-1">
                        <button
                            onClick={() => onGenderChange('female')}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium ${genderFilter === 'female' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                        >
                            Women
                        </button>
                        <button
                            onClick={() => onGenderChange('male')}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium ${genderFilter === 'male' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                        >
                            Men
                        </button>
                    </div>
                    <div className="flex bg-gray-100 rounded-full p-1">
                        <button
                            onClick={() => onSizeChange('normal')}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium ${sizeFilter === 'normal' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                        >
                            Normal
                        </button>
                        <button
                            onClick={() => onSizeChange('plus')}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium ${sizeFilter === 'plus' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                        >
                            Plus
                        </button>
                    </div>
                </div>
            </div>

            {loading && (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
                </div>
            )}

            {!loading && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
                    {filteredPoses.map((pose) => {
                        const isSelected = selectedPoses.some((pp) => pp.poseId === pose.poseId);
                        const order = isSelected ? selectedPoses.findIndex((p) => p.poseId === pose.poseId) + 1 : 0;
                        const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                        return (
                            <button
                                key={pose.poseId}
                                onClick={() => onTogglePose(pose)}
                                className={`group relative text-left transition-all ${isSelected ? 'scale-[0.98]' : 'hover:scale-[1.02]'}`}
                            >
                                <div className={`relative aspect-[3/4] rounded-xl sm:rounded-2xl overflow-hidden bg-gray-100 ${isSelected ? 'ring-4 ring-secondary-500' : ''}`}>
                                    <img
                                        src={imageUrl}
                                        alt={pose.type}
                                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200x280?text=Pose';
                                        }}
                                    />
                                    {isSelected && (
                                        <div className="absolute top-3 right-3 bg-secondary-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg text-xs font-bold">
                                            {order}
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {!loading && filteredPoses.length === 0 && <p className="text-sm text-gray-500 text-center py-8">No poses in this category.</p>}
        </div>
    );
};
